/**
 * FieldMapperTable — step 2 of the import wizard.
 * Lets the user map each source column to an investor or company field.
 */
import React from 'react';
import { INVESTOR_TARGET_FIELDS, COMPANY_TARGET_FIELDS } from '../types/importBatch';

interface FieldMapperTableProps {
  columns: string[];
  mapping: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  sample?: Record<string, any>[];
  targetType?: 'investor' | 'company';
}

const FieldMapperTable: React.FC<FieldMapperTableProps> = ({
  columns,
  mapping,
  onChange,
  sample = [],
  targetType = 'investor',
}) => {
  const targets = targetType === 'company' ? COMPANY_TARGET_FIELDS : INVESTOR_TARGET_FIELDS;
  const handleChange = (col: string, target: string) => {
    const next = { ...mapping };
    if (!target) {
      delete next[col];
    } else {
      next[col] = target;
    }
    onChange(next);
  };

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
              Source column
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
              Sample
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
              {targetType === 'company' ? 'Company field' : 'Investor field'}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {columns.map((col) => (
            <tr key={col}>
              <td className="px-4 py-2 text-sm font-medium text-gray-900">
                {col}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">
                {sample[0]?.[col] ?? ''}
              </td>
              <td className="px-4 py-2">
                <select
                  value={mapping[col] || ''}
                  onChange={(e) => handleChange(col, e.target.value)}
                  className="block w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">— ignore —</option>
                  {targets.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FieldMapperTable;
